import type { CSSProperties, ReactNode } from 'react';
import { AlarmClock, CalendarClock, Flag } from 'lucide-react';
import type { MissionSummary, Priority, Status, UserRef } from '@missions/types';
import { fromISODate, tMinus } from '@missions/ui';
import { useT } from '../lib/session';
import { AvatarImage } from './BrandLogo';

/* ---------- people ---------- */
export function Av({ u, size, tip = true }: { u: Pick<UserRef, 'name' | 'initials' | 'color' | 'avatarUrl'>; size?: number; tip?: boolean }) {
  const st: CSSProperties = { background: u.color };
  if (size) Object.assign(st, { width: size, height: size, fontSize: Math.round(size * 0.4) });
  return (
    <span className="av" style={st} data-tooltip={tip ? u.name : undefined} role="img" aria-label={u.name}>
      <AvatarImage src={u.avatarUrl} initials={u.initials} />
    </span>
  );
}
export function AvStack({ users, size = 24, max = 4 }: { users: UserRef[]; size?: number; max?: number }) {
  const shown = users.slice(0, max);
  const rest = users.length - shown.length;
  return (
    <span className="avs">
      {shown.map((u) => <Av key={u.id} u={u} size={size} />)}
      {rest > 0 && <span className="av more" style={{ width: size, height: size }} data-tooltip={users.slice(max).map((u) => u.name).join(', ')}>+{rest}</span>}
    </span>
  );
}

/* ---------- dates ---------- */
const fmtShort = (iso: string) => fromISODate(iso)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) ?? iso;
const fmtFull = (iso: string) => fromISODate(iso)?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) ?? iso;
export const fmtDate = fmtShort;
export const fmtDateLong = fmtFull;
const daysTo = (iso: string) => {
  const d = fromISODate(iso);
  if (!d) return 0;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 864e5);
};
export { daysTo };

/** Due date: T-minus chip with mission language, plain date otherwise (PLAN §10A). */
export function Due({ due, done }: { due: string | null; done?: boolean }) {
  const t = useT();
  if (!due) return <span className="empty-cell">—</span>;
  const d = fromISODate(due);
  if (!d) return <span className="empty-cell">—</span>;
  const diff = daysTo(due);
  const full = fmtFull(due);
  const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;
  if (t.tMinusDates) {
    const tm = tMinus(d, { done });
    const tip = done ? `Accomplished · was due ${full}` : diff < 0 ? `Overdue by ${days(-diff)} · was due ${full}` : diff === 0 ? `Due today · ${full}` : `Due ${full} · ${days(diff)} left`;
    return <span className={`ms-t ${tm.tone}`} data-tooltip={tip}>{tm.label}</span>;
  }
  if (done) return <span className="due">{fmtShort(due)}</span>;
  if (diff < 0) return <span className="due over" data-tooltip={`Overdue · was due ${full}`}><AlarmClock />{fmtShort(due)}</span>;
  if (diff === 0) return <span className="due today"><CalendarClock />Today</span>;
  if (diff === 1) return <span className="due">Tomorrow</span>;
  return <span className="due" data-tooltip={full}>{fmtShort(due)}</span>;
}

/* ---------- priority / status ---------- */
export const PRIORITY: Record<Priority, { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: '#e5484d' },
  high: { label: 'High', color: '#f2751a' },
  normal: { label: 'Normal', color: '#0457fd' },
  low: { label: 'Low', color: '#8a94a6' },
};
export function Pri({ p, compact }: { p: Priority; compact?: boolean }) {
  const m = PRIORITY[p];
  return (
    <span className="pri" data-tooltip={`Priority: ${m.label}`}>
      <Flag style={{ color: m.color, fill: m.color }} />
      <span className={compact ? 'pl sr' : 'pl'}>{m.label}</span>
    </span>
  );
}
export function SDot({ s }: { s: Pick<Status, 'name' | 'color' | 'isClosed'> | undefined }) {
  const name = s?.name ?? '—';
  if (s?.isClosed) return <span className="sdot done" role="img" aria-label={`Status: ${name}`} data-tooltip={`Status: ${name}`} />;
  return <span className="sdot" role="img" aria-label={`Status: ${name}`} data-tooltip={`Status: ${name}`} style={{ borderColor: s?.color ?? '#94a3b8' }} />;
}
export const SPill = ({ s }: { s: Pick<Status, 'name' | 'color'> }) => <span className="spill" style={{ background: s.color }}>{s.name}</span>;
export const Tag = ({ name, color }: { name: string; color: string }) => (
  <span className="tag" style={{ background: `color-mix(in srgb,${color} 14%,var(--surface))`, color }}>{name}</span>
);
export const MissionKey = ({ k }: { k: string }) => <span className="tid">{k}</span>;

/* ---------- loading / empty ---------- */
export const Radar = ({ size, style }: { size?: number; style?: CSSProperties }) => (
  <span className="ms-radar" style={{ ...(size ? ({ '--s': `${size}px` } as CSSProperties) : {}), ...style }} aria-hidden="true">
    <span className="blip" style={{ left: '68%', top: '30%' }} />
    <span className="blip" style={{ left: '28%', top: '62%', animationDelay: '.9s' }} />
  </span>
);
export function Empty({ title, text, actions, icon }: { title: string; text?: ReactNode; actions?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      {icon ? <div className="ill">{icon}</div> : <Radar size={96} />}
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {actions && <div className="acts">{actions}</div>}
    </div>
  );
}
export const Skel = ({ w, h = 12, style }: { w: string | number; h?: number; style?: CSSProperties }) => (
  <span className="sk ui-skel" style={{ width: w, height: h, display: 'inline-block', ...style }} />
);

/** "2m ago" style relative time + exact time tooltip */
export function rel(iso: string): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 6e4);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'Yesterday' : `${d}d ago`;
}
export const exact = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

export const isDone = (m: Pick<MissionSummary, 'completedAt'>) => !!m.completedAt;
export const etaLabel = (min: number | null) => (min == null ? '—' : `${Math.round((min / 60) * 10) / 10}h`);
